import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CircularProgress, Typography } from '@material-ui/core';
import { Box, useMediaQuery } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";

import useStyles from '../../../../../../assets/styles/ProgramPreviewStyles';
import { tUi } from "../../../../../../utils/dq_lc";
import { useColorMode } from "../../../../../../context/colorMode/colorMode.context";
import { useAuth } from '../../../../../../context/auth';

import { useProgramData } from '../../../hooks/LearnTab/useProgramData';
import { useSlideNavigation } from '../../../hooks/LearnTab/useSlideNavigation';
import { usePresentationTTS } from '../../../hooks/LearnTab/usePresentationTTS';
import { useSlideInlineEditor } from '../../../hooks/LearnTab/useSlideInlineEditor';
import { usePlanGating } from '../../../hooks/LearnTab/usePlanGating';

import DQProProgOutline from "../../../../DQProgramCreator/DQProProgOutline.page";
import Sidebar from './Sidebar/Sidebar.comp';
import TopBar from './TopBar/TopBar.comp';
import ContentArea from './ContentArea/ContentArea.comp';
import BottomNav from './ContentArea/BottomNav.comp';
import EditModeToolbar from './TopBar/EditModeToolbar.comp';
import TTSControlPopover from './TopBar/TTSControlPopover.comp';
import DQCompetencyDialog from '../Dialogs/DQCompetencyDialog.comp';

const LearnTab = ({
    embedded = false,
    programCode: programCodeProp,
    isHeaderCollapsed = false,
    onViewOutline,
    onSignUp,
    onUpgrade,
    focusMode = false,
    onToggleFocusMode,
    readOnly: readOnlyProp = false,
    isPublicPreview = false,
    canEdit = false,
}) => {
    const classes = useStyles();
    const { code: dq_module_code } = useParams();
    const code = programCodeProp || dq_module_code;
    const { authUser } = useAuth();
    const isGuest = !authUser || authUser.is_guest;
    const location = useLocation();
    const { setColorMode } = useColorMode();
    // Touch tablets in portrait (iPad 810–1024px) get the same stacked layout as phones: the desktop
    // layout's 26% outline + side padding left the slide too narrow (Yufei iPad portrait report).
    const isMobile = useMediaQuery("(max-width:780px), (hover: none) and (pointer: coarse) and (orientation: portrait) and (max-width:1024px)");

    useEffect(() => {
        if (isPublicPreview) {
            setColorMode('light');
        }
    }, [isPublicPreview, setColorMode]);

    const readOnly = readOnlyProp || location.state?.readOnly === true || isPublicPreview === true;

    const [showOutline, setShowOutline] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Nav actions bridge ref & landOnLastSlideRef
    const navRef = useRef({});
    const landOnLastSlideRef = useRef(false);

    const setSegmentsBridge = useCallback((val) => {
        if (navRef.current.setSegments) {
            navRef.current.setSegments(val);
        }
    }, []);
    const setActiveSegmentBridge = useCallback((val) => {
        if (navRef.current.setActiveSegment) {
            navRef.current.setActiveSegment(val);
        }
    }, []);
    const setCurrentSegmentBridge = useCallback((val) => {
        if (navRef.current.setCurrentSegment) {
            navRef.current.setCurrentSegment(val);
        }
    }, []);
    const setCurrentSlideBridge = useCallback((val) => {
        if (navRef.current.setCurrentSlide) {
            navRef.current.setCurrentSlide(val);
        }
    }, []);

    // Program Preview Data Hook
    const data = useProgramData({
        code,
        locationState: location.state,
        embedded,
        readOnly,
        isPublicPreview,
        setSegments: setSegmentsBridge,
        setActiveSegment: setActiveSegmentBridge,
        setCurrentSegment: setCurrentSegmentBridge,
        setCurrentSlide: setCurrentSlideBridge,
        landOnLastSlideRef,
    });

    // Slide Navigation Hook
    const nav = useSlideNavigation({
        data,
        setShowOutline,
        isMobile,
        isCollapsed,
        setIsCollapsed,
        landOnLastSlideRef,
    });

    // Populate bridge ref
    navRef.current = nav;

    // Plan Gating Hook
    const plan = usePlanGating({
        modulesData: data.modulesData,
        isPublicPreview,
    });

    // Slide Inline Editor Hook
    const editor = useSlideInlineEditor({
        code,
        data,
        nav,
        readOnly,
    });

    // Presentation TTS Hook
    const tts = usePresentationTTS({ nav, data });

    const toggleCollapse = () => setIsCollapsed(!isCollapsed);

    useEffect(() => {
        if (!embedded || isMobile) return;
        setIsCollapsed(focusMode);
    }, [embedded, isMobile, focusMode]);

    return (
        <Box sx={{
            mt: embedded ? 0 : (isPublicPreview ? 0 : { xs: 2, sm: '75px', lg: 2 }),
            overflowY: embedded ? 'hidden' : 'auto',
            flexDirection: { xs: 'column', sm: 'row' },
            display: 'flex',
            width: '100%',
            maxHeight: embedded ? '100%' : (isPublicPreview ? '100vh' : '82vh'),
            height: embedded ? '100%' : (isPublicPreview ? '100vh' : '82vh'),
            backgroundColor: isPublicPreview ? 'var(--brand-background, #ffffff)' : undefined,
            flex: embedded ? 1 : undefined,
            minHeight: embedded ? 0 : undefined,
        }}>
            {/* Left Sidebar */}
            <Sidebar
                data={data}
                nav={nav}
                plan={plan}
                classes={classes}
                code={code}
                embedded={embedded}
                isMobile={isMobile}
                isCollapsed={isCollapsed}
                toggleCollapse={toggleCollapse}
                isPublicPreview={isPublicPreview}
                isGuest={isGuest}
                onSignUp={onSignUp}
                onUpgrade={onUpgrade}
                onViewOutline={onViewOutline}
                setShowOutline={setShowOutline}
            />

            {/* Right Container */}
            {(!embedded || !isMobile || isCollapsed) && (
                <>
                    {data.initialLoading ? (
                        <Box className={classes.rightContainer} style={{
                            backgroundColor: embedded ? 'transparent' : '#fff',
                            display: 'flex',
                            height: embedded ? '100%' : '82vh',
                            overflow: 'scroll',
                            overflowX: 'hidden',
                            marginTop: embedded ? '0px' : '30px',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <CircularProgress size={60} style={{ color: '#ED6628', marginBottom: '20px' }} />
                            <Typography variant="h5" style={{ color: '#555', textAlign: 'center', maxWidth: '600px' }}>
                                Loading content... This might take a while as we prepare the learning materials.
                            </Typography>
                            <Typography variant="body1" style={{ color: '#777', textAlign: 'center', maxWidth: '600px', marginTop: '20px' }}>
                                Please be patient. The first load may take longer than usual, especially if content is being generated.
                            </Typography>
                        </Box>
                    ) : (data.selectedSession && data.selectedSection && nav.activeSegment) ? (
                        <Box className={classes.rightContainer} style={{
                            width: !isMobile ? (isCollapsed ? "100%" : "71%") : "100%",
                            height: embedded ? '100%' : '82vh',
                            marginTop: embedded ? '0px' : '30px',
                            maxWidth: '100%',
                            overflowX: 'hidden',
                            padding: embedded && showOutline ? '0px' : (isMobile ? '8px 12px' : '10px 100px 10px 70px'),
                        }}>
                            {/* Edit Mode Toolbar - Only show when multiple languages available */}
                            {editor.isEditMode && data.availableSlideLanguages.length > 1 && (
                                <EditModeToolbar
                                    currentEditLanguage={editor.currentEditLanguage}
                                    showEnglishContent={editor.showEnglishContent}
                                    onToggleLanguage={editor.handleLanguageToggle}
                                    uiLocale={data.uiLocale}
                                />
                            )}

                            {embedded && showOutline ? (
                                <Box className={classes.rightContentContainer} sx={{ overflowY: 'auto', p: 0, pt: '24px' }}>
                                    <DQProProgOutline
                                        embedded={true}
                                        programCode={code}
                                        isPublicPreview={isPublicPreview}
                                        initialDqcoList={data.dqcoList}
                                    />
                                </Box>
                            ) : (
                                <Box className={classes.rightContentContainer}>
                                    <TopBar
                                        nav={nav}
                                        data={data}
                                        editor={editor}
                                        tts={tts}
                                        isMobile={isMobile}
                                        embedded={embedded}
                                        onBackToSessions={() => { toggleCollapse(); setShowOutline(false); }}
                                        readOnly={readOnly}
                                        canEdit={canEdit}
                                        focusMode={focusMode}
                                        onToggleFocusMode={onToggleFocusMode}
                                        onSelectLanguage={editor.handleSelectLanguage || data.handleSelectLanguage}
                                    />

                                    <ContentArea
                                        data={data}
                                        nav={nav}
                                        editor={editor}
                                        tts={tts}
                                        classes={classes}
                                        isCollapsed={isCollapsed}
                                    />

                                    <TTSControlPopover
                                        tts={tts}
                                        uiLocale={data.uiLocale}
                                    />
                                </Box>
                            )}

                            {!(embedded && showOutline) && (
                                <BottomNav
                                    nav={nav}
                                />
                            )}
                        </Box>
                    ) : (
                        <Box className={classes.rightContainer} style={{ display: 'flex', alignItems: 'center', marginTop: embedded ? '0px' : '30px', justifyContent: 'center' }}>
                            <Typography variant="h5" style={{ color: '#777', textAlign: 'center' }}>
                                {tUi('preview.selectSession', data.uiLocale)}
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {/* DQ Competency Details Dialog */}
            <DQCompetencyDialog
                popupContent={data.popupContent}
                onClose={() => data.setPopupContent(null)}
            />
        </Box>
    );
};

export const DQProProgPreview = LearnTab;
export { LearnTab };
export default LearnTab;
